import { isMobileDevice } from './env.js';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const pickers = new WeakMap<HTMLInputElement, CustomDatePicker>();

// At most one picker is open at a time, so a single document listener (registered once)
// closes it on an outside click; per-instance listeners would leak with every lump row.
let openPicker: CustomDatePicker | null = null;
let outsideClickBound = false;

/** Marks `picker` as the open one (closing the previous) and binds the document listener once. */
function setOpenPicker(picker: CustomDatePicker): void {
  if (openPicker && openPicker !== picker) openPicker.close();
  openPicker = picker;
  if (outsideClickBound) return;
  outsideClickBound = true;
  document.addEventListener('click', event => {
    if (openPicker && !openPicker.contains(event.target as Node)) openPicker.close();
  });
}

export function getDatePicker(input: HTMLInputElement): CustomDatePicker | undefined {
  return pickers.get(input);
}

/** Wraps one month input in the custom picker (desktop only); idempotent. */
export function initDatePicker(input: HTMLInputElement): void {
  if (isMobileDevice || pickers.has(input)) return;
  pickers.set(input, new CustomDatePicker(input));
}

export function initDatePickers(form: HTMLFormElement): void {
  if (isMobileDevice) return;
  form.querySelectorAll<HTMLInputElement>('input[type="month"]').forEach(initDatePicker);
}

type YearMonth = { year: number; month: number };

/** `2026-03` → `{ year: 2026, month: 2 }` (0-based month); `null` for anything else. */
function parseYearMonth(value: string | null): YearMonth | null {
  if (!value) return null;
  const [year, month] = value.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return { year, month: month - 1 };
}

function isBefore(year: number, month: number, bound: YearMonth): boolean {
  return year < bound.year || (year === bound.year && month < bound.month);
}

function isAfter(year: number, month: number, bound: YearMonth): boolean {
  return year > bound.year || (year === bound.year && month > bound.month);
}

const FOCUSABLE = 'button:not([disabled]), select:not([disabled])';

function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
}

/** Selector that finds the same control again after `dropdown` is re-rendered; null when focus is elsewhere. */
function focusKeyOf(active: Element | null, dropdown: HTMLElement): string | null {
  if (!(active instanceof HTMLElement) || !dropdown.contains(active)) return null;
  if (active.dataset.action) return `[data-action="${active.dataset.action}"]`;
  if (active.dataset.month) return `[data-month="${active.dataset.month}"]`;
  if (active.dataset.months) return `[data-months="${active.dataset.months}"]`;
  if (active.classList.contains('date-picker-today')) return '.date-picker-today';
  return null;
}

function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month] = dateStr.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return dateStr;
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

export class CustomDatePicker {
  private readonly input: HTMLInputElement;
  private readonly wrapper: HTMLElement;
  private readonly dropdown: HTMLElement;
  private visualInput: HTMLInputElement | null = null;
  private currentYear: number;
  private currentMonth: number;
  private minDate: YearMonth | null = null;
  private maxDate: YearMonth | null = null;
  private isOpen = false;

  constructor(input: HTMLInputElement) {
    this.input = input;
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth();

    this.wrapper = document.createElement('div');
    this.wrapper.className = 'date-picker-wrapper';
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'date-picker-dropdown';

    this.createPicker();
    this.bindEvents();
    this.syncFromInput();
  }

  private createPicker(): void {
    this.input.parentNode?.insertBefore(this.wrapper, this.input);
    this.input.classList.add('date-picker-input');

    const visualInput = document.createElement('input');
    visualInput.type = 'text';
    visualInput.className = this.input.className;
    visualInput.value = formatDisplayDate(this.input.value);
    visualInput.readOnly = true;
    visualInput.setAttribute('data-original-input', 'true');
    visualInput.setAttribute('aria-haspopup', 'dialog');
    visualInput.setAttribute('aria-expanded', 'false');
    (['aria-label', 'aria-labelledby'] as const).forEach(name => {
      const value = this.input.getAttribute(name);
      if (value) visualInput.setAttribute(name, value);
    });
    const computedStyle = window.getComputedStyle(this.input);
    (['background', 'border', 'border-radius', 'padding', 'font', 'color'] as const).forEach(prop => {
      visualInput.style.setProperty(prop, computedStyle.getPropertyValue(prop));
    });
    this.wrapper.appendChild(visualInput);
    this.visualInput = visualInput;

    // The real input only carries the value: invisible, out of the tab order (Chromium would
    // otherwise expose three tab stops per month input) and hidden from assistive tech.
    this.input.style.position = 'absolute';
    this.input.style.opacity = '0';
    this.input.style.pointerEvents = 'none';
    this.input.style.width = '100%';
    this.input.style.height = '100%';
    this.input.style.zIndex = '1';
    this.input.tabIndex = -1;
    this.input.setAttribute('aria-hidden', 'true');
    this.wrapper.appendChild(this.input);

    this.dropdown.setAttribute('role', 'dialog');
    this.dropdown.setAttribute('aria-label', 'Выбор месяца');
    this.wrapper.appendChild(this.dropdown);
    this.render();
  }

  private render(): void {
    const focusedKey = focusKeyOf(document.activeElement, this.dropdown);
    const monthButtons = MONTH_NAMES.map((month, index) => {
      const isSelected = index === this.currentMonth;
      const isDisabled =
        (this.minDate !== null && isBefore(this.currentYear, index, this.minDate)) ||
        (this.maxDate !== null && isAfter(this.currentYear, index, this.maxDate));
      const classes = ['date-picker-month-btn', isSelected ? 'selected' : '', isDisabled ? 'disabled' : '']
        .filter(Boolean)
        .join(' ');
      return `<button type="button" class="${classes}" data-month="${index}">${month}</button>`;
    }).join('');

    const yearOptions: string[] = [];
    for (let year = this.currentYear - 10; year <= this.currentYear + 10; year += 1) {
      yearOptions.push(`<option value="${year}"${year === this.currentYear ? ' selected' : ''}>${year}</option>`);
    }

    // Static template: month names and years only, no user-provided text.
    this.dropdown.innerHTML = `
      <div class="date-picker-header">
        <button type="button" class="date-picker-nav" data-action="prev">‹</button>
        <div class="date-picker-month-year">
          <select class="date-picker-year-select" data-action="year-change">
            ${yearOptions.join('')}
          </select>
        </div>
        <button type="button" class="date-picker-nav" data-action="next">›</button>
      </div>
      <div class="date-picker-months-grid">
        ${monthButtons}
      </div>
      <div class="date-picker-footer">
        <button type="button" class="date-picker-today">Сегодня</button>
        <div class="date-picker-presets">
          <button type="button" class="date-picker-preset" data-months="3">+3 мес.</button>
          <button type="button" class="date-picker-preset" data-months="6">+6 мес.</button>
          <button type="button" class="date-picker-preset" data-months="12">+1 год</button>
        </div>
      </div>
    `;
    this.bindDropdownEvents();
    if (focusedKey) this.dropdown.querySelector<HTMLElement>(focusedKey)?.focus();
  }

  private bindEvents(): void {
    this.wrapper.addEventListener('click', event => {
      const target = event.target as HTMLElement;
      if (!this.isOpen && !target.closest('.date-picker-dropdown')) {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      }
    });
    this.dropdown.addEventListener('click', event => {
      event.stopPropagation();
    });
    this.wrapper.addEventListener('keydown', event => this.onKeydown(event));
    this.input.addEventListener('change', () => this.syncFromInput());
    this.input.addEventListener('input', () => this.syncFromInput());
  }

  private onKeydown(event: KeyboardEvent): void {
    const target = event.target;
    if (target === this.visualInput) {
      if (!this.isOpen && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
        event.preventDefault();
        this.open();
      } else if (this.isOpen && event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      }
      return;
    }
    if (!this.isOpen || !(target instanceof HTMLElement) || !this.dropdown.contains(target)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close(true);
    } else if (event.key === 'Tab') {
      // Leaving the dialog at either end closes it; focus then moves on from the field itself.
      const controls = focusableIn(this.dropdown);
      const edge = event.shiftKey ? controls[0] : controls[controls.length - 1];
      if (target === edge) this.close(true);
    }
  }

  private bindDropdownEvents(): void {
    this.dropdown.querySelectorAll<HTMLElement>('.date-picker-nav[data-action]').forEach(btn => {
      btn.addEventListener('click', event => {
        event.preventDefault();
        this.navigate(btn.dataset.action === 'prev' ? -1 : 1);
      });
    });

    const yearSelect = this.dropdown.querySelector<HTMLSelectElement>('.date-picker-year-select');
    yearSelect?.addEventListener('change', event => {
      event.preventDefault();
      event.stopPropagation();
      const newYear = parseInt(yearSelect.value, 10);
      if (!Number.isNaN(newYear)) {
        this.currentYear = newYear;
        this.render();
      }
    });

    this.dropdown.querySelectorAll<HTMLElement>('.date-picker-month-btn:not(.disabled)').forEach(btn => {
      btn.addEventListener('click', event => {
        event.preventDefault();
        this.selectMonth(parseInt(btn.dataset.month ?? '0', 10));
      });
    });

    this.dropdown.querySelector('.date-picker-today')?.addEventListener('click', event => {
      event.preventDefault();
      const today = new Date();
      this.currentYear = today.getFullYear();
      this.selectMonth(today.getMonth());
    });

    this.dropdown.querySelectorAll<HTMLElement>('.date-picker-preset').forEach(btn => {
      btn.addEventListener('click', event => {
        event.preventDefault();
        const months = parseInt(btn.dataset.months ?? '0', 10);
        const date = new Date();
        date.setMonth(date.getMonth() + months);
        this.currentYear = date.getFullYear();
        this.selectMonth(date.getMonth());
      });
    });
  }

  private navigate(direction: number): void {
    this.currentYear += direction;
    this.render();
  }

  private selectMonth(month: number): void {
    this.currentMonth = month;
    const dateStr = `${this.currentYear}-${String(month + 1).padStart(2, '0')}`;
    this.input.value = dateStr;
    this.updateVisual(dateStr);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.close(true);
  }

  private syncFromInput(): void {
    if (!this.input.value) return;
    const [year, month] = this.input.value.split('-').map(Number);
    if (Number.isFinite(year) && Number.isFinite(month)) {
      this.currentYear = year;
      this.currentMonth = month - 1;
    }
    this.updateVisual(this.input.value);
  }

  private updateVisual(dateStr: string): void {
    if (this.visualInput) {
      this.visualInput.value = formatDisplayDate(dateStr);
    }
  }

  /** True when `node` is inside the field or its dropdown. */
  contains(node: Node | null): boolean {
    return node !== null && this.wrapper.contains(node);
  }

  private open(): void {
    if (this.isOpen) return;
    setOpenPicker(this);
    this.isOpen = true;
    this.dropdown.classList.add('visible');
    this.visualInput?.setAttribute('aria-expanded', 'true');
    const rect = this.input.getBoundingClientRect();
    const dropdownHeight = 400;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      this.dropdown.style.bottom = '100%';
      this.dropdown.style.top = 'auto';
      this.dropdown.style.marginBottom = '4px';
      this.dropdown.style.marginTop = '0';
    } else {
      this.dropdown.style.top = '100%';
      this.dropdown.style.bottom = 'auto';
      this.dropdown.style.marginTop = '4px';
      this.dropdown.style.marginBottom = '0';
    }
    this.render();
    const selected = this.dropdown.querySelector<HTMLElement>('.date-picker-month-btn.selected:not(.disabled)');
    (selected ?? this.dropdown.querySelector<HTMLElement>('.date-picker-month-btn:not(.disabled)'))?.focus();
  }

  /** Closes the dropdown; `restoreFocus` puts the keyboard focus back on the field. */
  close(restoreFocus = false): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (openPicker === this) openPicker = null;
    this.dropdown.classList.remove('visible');
    this.visualInput?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.visualInput?.focus();
  }

  /** Months before `date` (YYYY-MM) cannot be picked; `null` lifts the bound. */
  setMinDate(date: string | null): void {
    this.minDate = parseYearMonth(date);
    if (this.isOpen) this.render();
  }

  /** Months after `date` (YYYY-MM) cannot be picked; `null` lifts the bound. */
  setMaxDate(date: string | null): void {
    this.maxDate = parseYearMonth(date);
    if (this.isOpen) this.render();
  }
}
